import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import localforage from "localforage";
import SideBar from "./SideBar";
import api from "../utils/axiosConfig";
import "../styles/welcomePage.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faCakeCandles,
  faFolder,
  faDiagramProject,
  faCalendarDay,
  faCalendarCheck,
  faCalendarPlus,
  faFileContract,
  faComments,
  faBriefcase,
  faChevronRight,
  faCheckCircle,
  faClock,
  faCircle,
  faSpinner,
  faUsersLine,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL;

// ── Week helpers (Wed → Tue, matching WorkspacePage) ──────────────────────────
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day >= 3 ? day - 3 : day + 4;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};
const getWeekEnd = (weekStart) => {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

// ── Status config (mirrors WorkspacePage) ─────────────────────────────────────
const STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "#666666", bg: "#F0F0F0", icon: faCircle },
  in_progress:  { label: "In Progress", color: "#E65100", bg: "#FFF3E0", icon: faClock },
  completed:    { label: "Completed",   color: "#2E7D32", bg: "#E8F5E9", icon: faCheckCircle },
};

/* ─────────────────────────────────────────────────────────
   Birthday Roller
───────────────────────────────────────────────────────── */
const BirthdayRoller = ({ birthdays, loading }) => {
  const [idx, setIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef(null);

  const advance = (dir = 1) => {
    if (!birthdays.length || animating) return;
    setAnimating(true);
    setTimeout(() => {
      setIdx((i) => (i + dir + birthdays.length) % birthdays.length);
      setAnimating(false);
    }, 280);
  };

  useEffect(() => {
    if (!birthdays.length) return;
    timerRef.current = setInterval(() => advance(1), 3500);
    return () => clearInterval(timerRef.current);
  }, [birthdays, animating]); // eslint-disable-line

  const fmt = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const current = birthdays[idx];

  return (
    <div className="bday-roller">
      <div className="bday-roller-icon">
        <FontAwesomeIcon icon={faCakeCandles} />
      </div>
      <div className="bday-roller-content">
        {loading ? (
          <span className="bday-roller-name">Loading…</span>
        ) : !birthdays.length ? (
          <span className="bday-roller-name" style={{ color: "var(--text-gray)" }}>
            No upcoming birthdays
          </span>
        ) : (
          <>
            <span className={`bday-roller-name ${animating ? "bday-roller-exit" : "bday-roller-enter"}`}>
              🎂 {current?.employeeName}
            </span>
            <span className="bday-roller-date">{fmt(current?.nextBirthday)}</span>
          </>
        )}
      </div>
      {birthdays.length > 1 && (
        <div className="bday-roller-controls">
          <span className="bday-counter">{idx + 1}/{birthdays.length}</span>
          <button onClick={() => advance(1)} className="bday-nav-btn">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Action Card
───────────────────────────────────────────────────────── */
const ActionCard = ({ to, icon, title, description, color, onClick }) => {
  const navigate = useNavigate();
  const handleClick = () => (onClick ? onClick() : navigate(to));
  return (
    <div
      className="wp-action-card"
      onClick={handleClick}
      style={{ "--card-accent": color || "var(--primary-color)" }}
    >
      <div className="wp-action-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="wp-action-body">
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
      <FontAwesomeIcon icon={faChevronRight} className="wp-action-arrow" />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   My Week Task Card — individual task row
───────────────────────────────────────────────────────── */
const WeekTaskCard = ({ task }) => {
  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.not_started;
  const total     = task.subtasks?.length || 0;
  const done      = task.subtasks?.filter((s) => s.status === "completed").length || 0;
  const pct       = total > 0 ? Math.round((done / total) * 100) : null;

  return (
    <div className="wt-card" style={{ "--task-color": cfg.color }}>
      {/* coloured left stripe */}
      <div className="wt-stripe" style={{ background: cfg.color }} />

      <div className="wt-body">
        {/* top row */}
        <div className="wt-top">
          <span className="wt-title">{task.title}</span>
          <span
            className="wt-badge"
            style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}
          >
            <FontAwesomeIcon icon={cfg.icon} style={{ fontSize: "0.6rem" }} />
            {cfg.label}
          </span>
        </div>

        {/* subtask progress */}
        {total > 0 && (
          <div className="wt-progress-wrap">
            <div className="wt-progress-bar">
              <div
                className="wt-progress-fill"
                style={{ width: `${pct}%`, background: cfg.color }}
              />
            </div>
            <span className="wt-progress-label">{done}/{total} subtasks</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   My Week Panel
───────────────────────────────────────────────────────── */
const MyWeekPanel = ({ tasks, loading }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="my-week-empty">
        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 20, color: "#1565C0" }} />
        <span>Loading tasks…</span>
      </div>
    );
  }

  if (!tasks.length) {
    return (
      <div className="my-week-empty">
        <div className="my-week-empty-icon">
          <FontAwesomeIcon icon={faCheckCircle} />
        </div>
        <span className="my-week-empty-title">All clear!</span>
        <span className="my-week-empty-sub">No tasks this week.</span>
        <button className="wp-link-btn" onClick={() => navigate("/workspace")}>
          <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: 6 }} />
          Go to Workspace
        </button>
      </div>
    );
  }

  // Summary counts
  const counts = {
    not_started: tasks.filter((t) => t.status === "not_started").length,
    in_progress: tasks.filter((t) => t.status === "in_progress").length,
    completed:   tasks.filter((t) => t.status === "completed").length,
  };

  return (
    <div className="my-week-content">
      {/* mini stat strip */}
      <div className="wt-stats">
        {Object.entries(counts).map(([key, val]) => {
          if (!val) return null;
          const cfg = STATUS_CONFIG[key];
          return (
            <div key={key} className="wt-stat" style={{ "--sc": cfg.color, "--sb": cfg.bg }}>
              <span className="wt-stat-num">{val}</span>
              <span className="wt-stat-label">{cfg.label}</span>
            </div>
          );
        })}
      </div>

      {/* task list — max 2 visible */}
      <div className="wt-list">
        {tasks.slice(0, 2).map((task) => (
          <WeekTaskCard key={task._id} task={task} />
        ))}
      </div>
      {tasks.length > 2 && (
        <p className="wt-overflow-hint">+{tasks.length - 2} more task{tasks.length - 2 > 1 ? "s" : ""} this week</p>
      )}

      <button className="wp-link-btn" onClick={() => navigate("/workspace")}>
        <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: 6 }} />
        View all in Workspace
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Main WelcomePage
───────────────────────────────────────────────────────── */
const WelcomePage = () => {
  const [role, setRole]                       = useState("");
  const [userName, setUserName]               = useState("");
  const [error, setError]                     = useState("");
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [birthdayLoading, setBirthdayLoading] = useState(false);
  const [allTasks, setAllTasks]               = useState([]);
  const [tasksLoading, setTasksLoading]       = useState(false);

  /* Role + token check */
  useEffect(() => {
    const init = async () => {
      try {
        const [storedRole, token, storedName] = await Promise.all([
          localforage.getItem("role1"),
          localforage.getItem("token"),
          localforage.getItem("userName"),
        ]);
        if (storedRole) setRole(storedRole);
        if (storedName) setUserName(storedName);
        if (!token) setError("Session expired. Please login again.");
      } catch {
        setError("Failed to initialise session.");
      }
    };
    init();
  }, []);

  /* Upcoming birthdays */
  useEffect(() => {
    const fetchBirthdays = async () => {
      setBirthdayLoading(true);
      try {
        const token = await localforage.getItem("token");
        if (!token) return;
        const res = await api.get(
          `${API_URL}/leavedata/upcoming-birthdays?days=60&limit=10`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUpcomingBirthdays(res.data?.upcoming || []);
      } catch {
        setUpcomingBirthdays([]);
      } finally {
        setBirthdayLoading(false);
      }
    };
    fetchBirthdays();
  }, []);

  /* All tasks — filtered to current week client-side */
  useEffect(() => {
    const fetchTasks = async () => {
      setTasksLoading(true);
      try {
        const token = await localforage.getItem("token");
        if (!token) return;
        const res = await api.get(`${API_URL}/tasks/my-tasks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllTasks(res.data?.data || []);
      } catch {
        setAllTasks([]);
      } finally {
        setTasksLoading(false);
      }
    };
    fetchTasks();
  }, []);

  // ── Filter to current Wed→Tue window ─────────────────────────────────────
  const myWeekTasks = useMemo(() => {
    const cwStart = getWeekStart(new Date());
    const cwEnd   = getWeekEnd(cwStart);
    return allTasks.filter((t) => {
      const d = new Date(t.createdAt || new Date());
      return d >= cwStart && d <= cwEnd;
    });
  }, [allTasks]);

  const isMod = role === "mod";

  return (
    <>
      <SideBar />
      <div className="dashboard-container wp-root">

        {/* ── Header ───────────────────────────────────────── */}
        <div className="wp-header">
          <div className="wp-header-left">
            <div className="wp-welcome-row">
              <h1 className="wp-welcome">Welcome!</h1>
              {userName && <span className="wp-username">{userName}</span>}
            </div>
            {error && <div className="alert alert-error" style={{ marginTop: 8 }}>{error}</div>}
          </div>
          <div className="wp-header-right">
            <BirthdayRoller birthdays={upcomingBirthdays} loading={birthdayLoading} />
          </div>
        </div>

        {/* ── Main 2-column grid ───────────────────────────── */}
        <div className="wp-grid">

          {/* LEFT column */}
          <div className="wp-col-left">

            {/* Top Quick Actions */}
            <section className="wp-section">
              <div className="wp-top-actions">
                <ActionCard to="/workspace" icon={faDiagramProject} title="Plan My Week"
                  description="Organise tasks for this week" color="#1565C0" />
                <ActionCard to="/workspace?tab=chat" icon={faComments} title="Chat"
                  description="Team messages & threads" color="#0097A7" />
                <ActionCard to="/maha-holiday-calendar" icon={faCalendarAlt} title="Calendar"
                  description="Holidays & schedules" color="#6D4C41" />
                {isMod && (
                  <ActionCard to="/userdashboard" icon={faUsersLine} title="User Management"
                    description="Users & permissions" color="#E83E8C" />
                )}
                {isMod && (
                  <ActionCard to="/influencer-leaderboard" icon={faFolder} title="Influencer Dashboard"
                    description="Insights & analytics" color="#863ee8" />
                )}
              </div>
            </section>

            {/* Leave Management */}
            <section className="wp-section">
              <div className="wp-section-header">
                <FontAwesomeIcon icon={faCalendarAlt} className="wp-section-icon" />
                <h2 className="wp-section-title">Leave Management</h2>
              </div>
              <div className="wp-leave-grid">
                <ActionCard to="/leave-data" icon={faCalendarDay} title="Check Leave"
                  description="View your leave history" color="#CE452B" />
                <ActionCard to="/leave-form" icon={faCalendarPlus} title="Request Leave"
                  description="Submit a new application" color="#2E7D32" />
                <ActionCard to="/leave-data-rm" icon={faCalendarCheck} title="Leave Approvals"
                  description="Approve or reject requests" color="#F57C00" />
                <ActionCard
                  onClick={() => window.open(
                    "https://akar-portal-data.s3.ap-south-1.amazonaws.com/AKAR_Attendance_Leave_Policy+(1).pdf",
                    "_blank"
                  )}
                  icon={faFileContract} title="Leave Policy"
                  description="View policy document" color="#5C6BC0" />
              </div>
            </section>

          </div>

          {/* RIGHT column — My Week */}
          <div className="wp-col-right">
            <div className="my-week-panel">
              <div className="my-week-header">
                <div className="my-week-header-left">
                  <div className="my-week-icon-wrap">
                    <FontAwesomeIcon icon={faBriefcase} />
                  </div>
                  <div>
                    <h2 className="my-week-title">My Week</h2>
                    <p className="my-week-sub">Current tasks</p>
                  </div>
                </div>
                {myWeekTasks.length > 0 && (
                  <span className="my-week-count">{myWeekTasks.length}</span>
                )}
              </div>
              <div className="my-week-scroll">
                <MyWeekPanel tasks={myWeekTasks} loading={tasksLoading} />
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default WelcomePage;